import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Search, MapPin, Star, Loader2, Check } from "lucide-react";
import { track } from "@/lib/events";
import { referralCode } from "@/lib/events";
import { useSeo } from "@/lib/seo";

const INDUSTRIES = ["HVAC & Home Comfort", "Plumbing", "Electrical", "Landscaping", "Roofing", "Cleaning Services", "Auto Repair", "Dental", "Med Spa / Aesthetics", "Restaurant / Cafe", "Fitness / Gym", "Salon / Barber", "Real Estate", "Legal Services", "Accounting", "Retail Store", "Other"];

const ONLINE_INDUSTRIES = ["SaaS / Software", "E-commerce / Online Store", "Online Education / Courses", "Trading / Investing Education", "Fintech / Finance", "Digital Marketing Agency", "Coaching / Consulting", "Content / Media / Newsletter", "Mobile App", "Marketplace / Platform", "Creator / Influencer", "Online Services", "Other"];

export default function Onboarding() {
  useSeo({
    title: "Run a free AI competitive analysis of your business · A.R.I.A.",
    description: "Tell A.R.I.A. about your business — or find it on Google — and get an AI competitive analysis with a prioritized 30-day growth plan in minutes. Free to start.",
    path: "/analyze",
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [scope, setScope] = useState("local");
  const [form, setForm] = useState({
    business_name: location.state?.business_name || "",
    website: location.state?.website || "",
    industry: "HVAC & Home Comfort",
    city: "",
    state: "",
    main_service: "",
    customer_type: "",
    competitor_urls: "",
  });

  const isOnline = scope === "online";
  const industryList = isOnline ? ONLINE_INDUSTRIES : INDUSTRIES;

  const setScopeAndDefaults = (next) => {
    setScope(next);
    setForm((f) => ({ ...f, industry: (next === "online" ? ONLINE_INDUSTRIES : INDUSTRIES)[0] }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [bizQuery, setBizQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [acOpen, setAcOpen] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [pickedPlace, setPickedPlace] = useState(null);
  const [googlePlace, setGooglePlace] = useState(null);
  const acTimer = useRef(null);

  // Prefill from the landing-page Google business search.
  useEffect(() => {
    const p = location.state?.place;
    if (!p) return;
    const parts = (p.address || "").split(",").map((x) => x.trim());
    let city = "", state = "";
    if (parts.length >= 3) { city = parts[parts.length - 3]; state = (parts[parts.length - 2] || "").split(" ")[0]; }
    setForm((f) => ({ ...f, business_name: p.name || f.business_name, city: city || f.city, state: state || f.state }));
    setGooglePlace(p); setPickedPlace(p); setBizQuery(p.name || "");
  }, [location.state]);

  useEffect(() => {
    if (acTimer.current) clearTimeout(acTimer.current);
    const q = bizQuery.trim();
    if (q.length < 3 || (pickedPlace && q === pickedPlace.name)) { setSuggestions([]); setAcOpen(false); return; }
    acTimer.current = setTimeout(async () => {
      setAcLoading(true);
      try {
        const { data } = await api.post("/places/autocomplete", { input: q });
        setSuggestions(data.suggestions || []);
        setAcOpen(true);
      } catch { /* silent */ } finally { setAcLoading(false); }
    }, 320);
    return () => acTimer.current && clearTimeout(acTimer.current);
  }, [bizQuery]);

  const pickSuggestion = async (s) => {
    setAcOpen(false); setBizQuery(s.main);
    try {
      const { data } = await api.get(`/places/details/${s.place_id}`);
      const p = data.place;
      const parts = (p.address || "").split(",").map((x) => x.trim());
      let city = "", state = "";
      if (parts.length >= 3) { city = parts[parts.length - 3]; state = (parts[parts.length - 2] || "").split(" ")[0]; }
      setForm((f) => ({
        ...f,
        business_name: p.name || f.business_name,
        website: (p.website || f.website || "").replace(/^https?:\/\//, "").split("?")[0],
        city: city || f.city,
        state: state || f.state,
      }));
      setGooglePlace(p);
      setPickedPlace(p);
      toast.success(`Loaded ${p.name} — details prefilled below.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load that business.");
    }
  };

  const submit = async () => {
    const missingCity = !isOnline && !form.city;
    if (!form.business_name || !form.website || !form.main_service || missingCity) {
      toast.error(isOnline
        ? "Please fill in business name, website and main service."
        : "Please fill in business name, website, city and main service.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        business_scope: scope,
        referral_code: referralCode() || undefined,
        google_place: googlePlace || undefined,
        competitor_urls: form.competitor_urls.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/analyze", payload);
      track("scan_started", {
        analysis_id: data.analysis_id,
        landing_variant: localStorage.getItem("oa_landing_variant") || undefined,
      });
      navigate(`/analyzing/${data.analysis_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start analysis.");
      setSubmitting(false);
    }
  };

  const field = (k, label, ph, req) => (
    <div>
      <Label className="mono-label">{label}{req && " *"}</Label>
      <Input
        data-testid={`onboard-${k}`}
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        placeholder={ph}
        className="mt-2 h-12 rounded-none border-black/20 focus-visible:ring-klein"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Navbar />
      <div className="mx-auto max-w-3xl px-5 py-10 sm:py-16 lg:px-8">
        <span className="mono-label">Onboarding</span>
        <h1 className="mt-3 font-display text-3xl tracking-tight sm:text-4xl">Tell us about your business</h1>
        <p className="mt-3 text-slate-600">This takes under a minute. We use it to research your market and competitors.</p>

        {/* Find your business on Google (Places autocomplete) */}
        <div className="mt-8 border border-klein/30 bg-klein/5 p-5" data-testid="place-search">
          <div className="flex items-center gap-2">
            <Search size={16} className="text-klein" />
            <span className="mono-label !text-klein">Find your business on Google (optional)</span>
          </div>
          <p className="mt-1 text-sm text-slate-600">Start typing your business name — pick it to auto-fill your exact name, website, location, rating &amp; reviews.</p>
          <div className="relative mt-3">
            <div className="relative">
              <Input
                data-testid="place-search-input"
                value={bizQuery}
                onChange={(e) => setBizQuery(e.target.value)}
                onFocus={() => suggestions.length && setAcOpen(true)}
                placeholder="e.g. Northside Comfort Co. Denver"
                className="h-12 rounded-none border-black/20 bg-white pr-10 focus-visible:ring-klein"
                autoComplete="off"
              />
              {acLoading && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-klein" />}
            </div>
            {acOpen && suggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full border border-black/15 bg-white shadow-lg" data-testid="place-results">
                {suggestions.map((s) => (
                  <button key={s.place_id} data-testid={`place-result-${s.place_id}`} type="button" onClick={() => pickSuggestion(s)}
                    className="flex w-full items-start gap-2 border-b border-black/5 px-3 py-2.5 text-left last:border-0 hover:bg-klein/5">
                    <MapPin size={14} className="mt-0.5 shrink-0 text-klein" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-brand-dark">{s.main}</div>
                      {s.secondary && <div className="truncate text-xs text-slate-500">{s.secondary}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {pickedPlace && (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-success" data-testid="place-picked">
              <span className="inline-flex items-center gap-1"><Check size={14} /> Loaded <b>{pickedPlace.name}</b></span>
              {pickedPlace.rating != null && <span className="inline-flex items-center gap-0.5 text-slate-600"><Star size={12} className="text-warning" /> {pickedPlace.rating} · {pickedPlace.reviews_count || 0} reviews</span>}
              {pickedPlace.categories?.[0] && <span className="text-slate-500">{pickedPlace.categories[0].replace(/_/g, " ")}</span>}
            </div>
          )}
        </div>

        {/* Business type toggle */}
        <div className="mt-8">
          <Label className="mono-label">What kind of business is this?</Label>
          <div className="mt-2 inline-flex border border-black/20" data-testid="scope-toggle">
            <button
              type="button"
              data-testid="scope-local"
              onClick={() => setScopeAndDefaults("local")}
              className={`px-5 py-2.5 text-sm font-medium transition-colors ${!isOnline ? "bg-brand-dark text-white" : "bg-white text-slate-600 hover:text-ink"}`}
            >Local business</button>
            <button
              type="button"
              data-testid="scope-online"
              onClick={() => setScopeAndDefaults("online")}
              className={`px-5 py-2.5 text-sm font-medium transition-colors ${isOnline ? "bg-brand-dark text-white" : "bg-white text-slate-600 hover:text-ink"}`}
            >Online / National</button>
          </div>
          <p className="mt-2 mono-label !text-slate-500">
            {isOnline ? "For SaaS, e-commerce, online education, digital services — no physical location needed." : "For businesses that serve customers in a specific city or area."}
          </p>
        </div>

        <div className="mt-8 grid gap-6 border border-black/10 bg-white p-6 sm:grid-cols-2 sm:p-8">
          <div className="sm:col-span-2">{field("business_name", "Business name", isOnline ? "Angry Charts" : "Northside Comfort Co.", true)}</div>
          <div className="sm:col-span-2">{field("website", "Website", "yourbusiness.com", true)}</div>
          <div>
            <Label className="mono-label">Industry / category *</Label>
            <select
              data-testid="onboard-industry"
              value={form.industry}
              onChange={(e) => set("industry", e.target.value)}
              className="mt-2 h-12 w-full rounded-none border border-black/20 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-klein"
            >
              {industryList.map((i) => <option key={i}>{i}</option>)}
            </select>
          </div>
          {field("main_service", "Main service / product", isOnline ? "Trading charts & market education" : "HVAC installation & repair", true)}
          {isOnline
            ? field("city", "Primary market / region (optional)", "e.g. United States, Global")
            : field("city", "City", "Denver", true)}
          {isOnline
            ? field("state", "Languages / regions served (optional)", "English, US & EU")
            : field("state", "State / region", "CO")}
          <div className="sm:col-span-2">{field("customer_type", "Primary customer type", isOnline ? "Retail traders & investors" : "Residential homeowners")}</div>
          <div className="sm:col-span-2">{field("competitor_urls", "Competitor URLs (optional, comma-separated)", "competitor1.com, competitor2.com")}</div>
        </div>

        <Button
          data-testid="onboard-submit"
          onClick={submit}
          disabled={submitting}
          className="mt-8 h-14 w-full rounded-none bg-brand-dark text-base text-white hover:bg-klein sm:w-auto sm:px-10"
        >
          {submitting ? "Starting analysis…" : "Run My Free Analysis"} <ArrowRight size={18} className="ml-2" />
        </Button>
        <p className="mt-3 mono-label">If you don't add competitors, our AI will identify likely ones.</p>
      </div>
    </div>
  );
}
